import React, { useCallback, useEffect, useState } from 'react';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';
import { Elements } from 'react-stripe-elements';
import PropTypes from 'prop-types';
import { ReactComponent as Loader } from './../../projectPost/PublishedProposals/loader.svg';
import { useAuth } from '../../../hooks/useAuth';
import { getReviewByPublisherUid, updateReview } from '../../../firebase/api/review';
import PortfolioForm from './portfolioForm/PortfolioForm';
import { updatePost } from '../../../firebase/api/post';

import './AddPortfolioToInvitePopup.css';
import { useActions } from '../../../hooks/useActions';

const rawInitialState = {
    comment: '',
    portfolioLinks: [
        {
            name: 'Website',
            link: '',
        },
        {
            name: 'Youtube',
            link: '',
        },
    ],
    files: [],
};

const popupSteps = {
    brief: 'brief',
    portfolio: 'portfolio',
};

const AddPortfolioToInvitePopup = ({
    postId,
    postData,
    briefLink,
    isOpen,
    handleToggle,
    handleResponseForInvite,
    isProjectPostPage,
    subCategoryTitle,
    onlyReadableBrief = false,
}) => {
    const { currentUser } = useAuth();
    const [review, setReview] = useState(null);
    const [portfolioInitialState, setPortfolioInitialState] = useState(rawInitialState);
    const [isAlreadyAcceptInvite, setIsAlreadyAcceptInvite] = useState(false);
    const [loading, setLoading] = useState(true);
    const [currentScreen, setCurrentScreen] = useState(popupSteps.brief);
    const { changePostAfterAnswer, changePostAfterAnswerOnProjectPostPage } = useActions();

    useEffect(() => {
        getReviewByPublisherUid(postId, currentUser.uid).then(res => {
            if (res) {
                setIsAlreadyAcceptInvite(true);
                setReview(res);
                !!res.portfolioData && setPortfolioInitialState(res.portfolioData);
            }
            if (!onlyReadableBrief && (!briefLink || !!res?.portfolioData))
                setCurrentScreen(popupSteps.portfolio);
            setLoading(false);
        });
    }, [currentUser, postId, briefLink, onlyReadableBrief]);

    const handleSubmit = useCallback(
        async data => {
            const portfolioData = { ...data };
            if (isAlreadyAcceptInvite) {
                const reviewDto = { ...review, portfolioData };
                const postUpdatedDto = {
                    ...postData,
                    responseEmails: [
                        ...postData.responseEmails.map(response =>
                            response.email === currentUser.email
                                ? {
                                      ...response,
                                      isPortfolioUploaded: true,
                                  }
                                : response
                        ),
                    ],
                };
                await Promise.all([updateReview(reviewDto), updatePost(postUpdatedDto)]).then(
                    () => {
                        isProjectPostPage
                            ? changePostAfterAnswerOnProjectPostPage({
                                  rawPostData: postUpdatedDto,
                                  newReview: reviewDto,
                              })
                            : changePostAfterAnswer({
                                  rawPostData: postUpdatedDto,
                                  subCategoryTitle,
                              });
                    }
                );
                //   updateReview(reviewDto);
                // await updatePost(postUpdatedDto);
            } else {
                await handleResponseForInvite({
                    portfolioData,
                    responseType: 'accept',
                });
            }
            handleToggle();
        },
        [
            review,
            handleToggle,
            handleResponseForInvite,
            isAlreadyAcceptInvite,
            postData,
            currentUser.email,
            isProjectPostPage,
            subCategoryTitle,
            changePostAfterAnswer,
            changePostAfterAnswerOnProjectPostPage,
        ]
    );

    const handleResponseForInviteWithoutPortfolio = useCallback(async () => {
        !isAlreadyAcceptInvite && (await handleResponseForInvite({ responseType: 'accept' }));
        handleToggle();
    }, [handleResponseForInvite, handleToggle, isAlreadyAcceptInvite]);

    const handleDecline = useCallback(async () => {
        handleResponseForInvite({ responseType: 'decline' });
        handleToggle();
    }, [handleResponseForInvite, handleToggle]);

    return (
        <div>
            <Modal isOpen={isOpen} toggle={handleToggle} className="addPortfolioPopup">
                {loading ? (
                    <div className="popup-loader">
                        <Loader />
                    </div>
                ) : (
                    <>
                        <ModalHeader className="add-portfolio-header" toggle={handleToggle}>
                            {currentScreen === popupSteps.brief ? 'The Brief' : 'Submit your files'}
                        </ModalHeader>

                        <ModalBody>
                            <Elements>
                                <PortfolioForm
                                    postId={postId}
                                    handleSubmit={handleSubmit}
                                    handleResponseForInviteWithoutPortfolio={
                                        handleResponseForInviteWithoutPortfolio
                                    }
                                    handleDecline={handleDecline}
                                    portfolioInitialState={portfolioInitialState}
                                    currentScreen={currentScreen}
                                    setCurrentScreen={setCurrentScreen}
                                    onlyReadable={onlyReadableBrief}
                                    briefLink={briefLink}
                                />
                            </Elements>
                        </ModalBody>
                    </>
                )}
            </Modal>
        </div>
    );
};

AddPortfolioToInvitePopup.propTypes = {
    postId: PropTypes.string.isRequired,
    postData: PropTypes.object.isRequired,
    briefLink: PropTypes.any,
    isOpen: PropTypes.bool.isRequired,
    handleToggle: PropTypes.func.isRequired,
    handleResponseForInvite: PropTypes.func.isRequired,
    isProjectPostPage: PropTypes.bool.isRequired,
    subCategoryTitle: PropTypes.any.isRequired,
    onlyReadableBrief: PropTypes.bool.isRequired,
};

export default AddPortfolioToInvitePopup;
